export const PROPERTIES = {
  ATHLETE_COUNT: "athleteCount",
  EVENT_PROPERTY_APPLICATION: "application",
  USER_PROPERTY_ORGANISATION_ID: "organisationId",
  USER_PROPERTY_EMAIL: "email",
  USER_PROPERTY_FIRST_SEEN: "hubFirstSeen",
  USER_PROPERTY_LAST_SEEN: "hubLastSeen",
  SCREEN_WIDTH: "screenWidth", // Number
  SCREEN_HEIGHT: "screenHeight", // Number
  SCREEN_RESOLUTION: "screenResolution", // String, e.g. "1920x1080"
  VIEWPORT_WIDTH: "viewportWidth", // Number
  VIEWPORT_HEIGHT: "viewportHeight", // Number
  VIEWPORT_SIZE: "viewportSize", // String, e.g. "1440x900"
  CHART_TYPE: "chartType",
  VISUALIZATION_TYPE: "visType",
  EXERCISE_ID: "exerciseId", // String
  EXERCISE_CATEGORY: "exerciseCategory", // String
  METRIC_FIELD: "metricField", // String
  PROFILE_TYPE: "profileType", // velocity, power
  PERIOD: "period", // day, week
  FILTER_GROUP_COUNT: "filterGroupCount", // Number
  FILTER_ATHLETE_COUNT: "filterAthleteCount", // Number
  EVENT_CLICK_LOCATION: "eventClickLocation", // leaderboardExerciseSelector, liveActivityFeed

  CHARTING_MEASURE_COUNT: "measureCount",
  CHARTING_SOURCE_COUNT: "sourceCount",
  CHARTING_MEASURE_ID: "measureId",
  CHARTING_MEASURE_TYPE: "measureType",

  WORKOUTS_COUNT: "workoutsCount", // Number
  WORKOUTS_LABEL_ASSIGN_FLAG: "workoutsLabelAssignFlag", // Boolean
  WORKOUTS_LABEL_FILTERS_COUNT: "workoutsLabelFiltersCount", // Number

  CUSTOM_EXERCISE_DERIVED_FROM_ID: "derivedFromExerciseId",
  CUSTOM_EXERCISE_IS_WEIGHTED: "isWeighted",
  CUSTOM_EXERCISE_IS_REP_BASED: "isRepBased",
  CUSTOM_EXERCISE_IS_BILATERAL: "isBilateral",
  CUSTOM_EXERCISE_IS_MIGRATION: "isMigration",
  CUSTOM_EXERCISE_IS_SENSOR_EXERCISE: "isSensorExercise",
  CUSTOM_EXERCISE_IS_DERIVED_FROM_EXERCISE_ID_UPDATED: "isDerivedFromExerciseIdUpdated",
  CUSTOM_EXERCISE_IS_MOVEMENT_TYPE_UPDATED: "isMovementTypeUpdated",
  CUSTOM_EXERCISE_LOCATION: "location",
  PAGE: "page",
  DASHBOARD_TYPE: "type",
  DASHBOARD_GLOBAL: "isGlobal",
  DASHBOARD_WIDGET_TYPE: "widgetType",

  GROUP_MEMBER_COUNT: "groupMemberCount",
  GROUP_MEMBER_SELECTED_COUNT: "groupMemberSelectedCount",
  DASHBOARD_REPORT_COUNT: "reportCount",
  DASHBOARD_REPORT_PERCENTAGE_WITH_NOTE: "percentageWithNote",
  DASHBOARD_REPORT_INCLUDE_ANTHROPOMETRICS: "includeAnthropometrics",

  BULK_UPLOAD_ATHLETE_COUNT: "athleteCount",
  BULK_UPLOAD_REQUEST_TIME: "requestTime",
  BULK_UPLOAD_STATUS: "status",

  PROGRAM_ID: "programId",
  FILTER_SEARCH_TEXT: "searchText",
  FILTER_CREATED_BY: "createdBy", // String (userId)
  FILTER_SCHEDULE_FOR: "scheduleFor", // String (athleteId or groupId)
  SORT_BY: "sortBy", // name, updatedAt
  WORKOUT_TASK_TYPE: "taskType", // Exercise, Note, Video
  CATEGORY: "category",
  DATE: "date",
  DAY_ID: "dayId",
  WEEK_ID: "weekId",
  EVENT_ID: "eventId",
  EVENT_KIND: "eventKind", // Athlete, Group, AthleteOverride

  DRAGGABLE_TYPE: "draggableType", // Usually the type of the item being dragged, such as workout, week, event, task...
  ORIGIN: "origin", // Used to indicate the [origin/location] of an action
  EVENT_SOURCE_TYPE: "eventSourceType", // program, (standalone/workout)
  EVENT_TYPE: "eventType", // workout, restDay

  UPSELL_FEATURE: "upsellFeature", // OutputProgram, GroupsAndMonitoring
  UPSELL_PLAN_TYPE: "upsellPlanType", // Base, Core, ... or "Legacy" when the org has no subscription
  TIME_SPENT_SECONDS: "timeSpentSeconds", // Number

  FILE_TYPE: "fileType", // pdf, csv
  IMPORT_MATCHED_EXERCISE_COUNT: "matchedExerciseCount", // Number
  IMPORT_UNMATCHED_EXERCISE_COUNT: "unmatchedExerciseCount", // Number
  IMPORT_MATCHED_CIRCUIT_COUNT: "matchedCircuitCount", // Number
  IMPORT_UNMATCHED_CIRCUIT_COUNT: "unmatchedCircuitCount", // Number
  IMPORT_CIRCUIT_STEPS_COUNT: "circuitStepsCount", // Number
  IMPORT_TARGET: "importTarget", // newProgram, existingProgram

  TRACE_ID: "traceId", // Arize trace ID for the LLM generation this event relates to
  AI_SUMMARY_FEEDBACK_VALUE: "feedbackValue", // up, down
} as const;

const ATHLETE_EVENTS = {
  ATHLETE_DATA_EXPORT: "hub:athletes:dataExport",
  ATHLETE_COMPLETED_WORKOUT: "hub:athlete:completedWorkout",
  ATHLETE_CREATE: "hub:athlete:create",
  ATHLETE_BULK_UPLOAD_AI: "hub:athlete:bulkUploadAI",
  ATHLETES_VIEW_ALL: "hub:athletes:viewAll",
  ATHLETES_VIEW_PROFILE: "hub:athletes:viewProfile",
  ATHLETE_PROFILE_VIEW_DATA: "hub:athleteProfile:viewChart",
  ATHLETE_PROFILE_VIEW_1RM_MODAL: "hub:athleteProfile:view1RMModal",
  /** Athlete charting view: Logs when a user changes the metric in the chart controls. */
  ATHLETE_CHARTING_VIEW_METRIC: "hub:athleteChartingControls:metric", // PROPERTIES: chartType, visType, metricField, exerciseId, exerciseCategory
  /** Athlete charting view: Logs when a user changes the rep metric in the chart controls. */
  ATHLETE_CHARTING_VIEW_REP_METRIC: "hub:athleteChartingControls:repMetric", // PROPERTIES: chartType, visType, metricField, exerciseId, exerciseCategory
  /** Athlete charting view: Logs when a user changes the profile type in the chart controls. */
  ATHLETE_CHARTING_VIEW_PROFILE_TYPE: "hub:athleteChartingControls:profileType", // PROPERTIES: chartType, visType, profileType, metricField, exerciseId, exerciseCategory
  /** Athletes - Athlete charting updates announcement dialog shown */
  ATHLETE_VELOCITY_ANNOUNCEMENT_VIEW: "hub:athletes:velocityAnnouncementView",
  /** Athletes - Athlete charting updates announcement dialog dismissed */
  ATHLETE_VELOCITY_ANNOUNCEMENT_DISMISS: "hub:athletes:velocityAnnouncementDismiss", // PROPERTIES: origin (closeButton, escapeKey, gotItButton)
  /** Athlete profile - Velocity-based 1RM onboarding tooltip shown */
  ATHLETE_PROFILE_ONE_REP_MAX_VELOCITY_ONBOARDING_VIEW: "hub:athleteProfile:oneRepMaxVelocityOnboardingView",
  /** Athlete profile - Velocity-based 1RM onboarding tooltip dismissed */
  ATHLETE_PROFILE_ONE_REP_MAX_VELOCITY_ONBOARDING_DISMISS: "hub:athleteProfile:oneRepMaxVelocityOnboardingDismiss", // PROPERTIES: origin (closeButton, escapeKey)
  /** Athlete profile - Learn more link clicked in the velocity-based 1RM onboarding tooltip */
  ATHLETE_PROFILE_ONE_REP_MAX_VELOCITY_ONBOARDING_LEARN_MORE_CLICK:
    "hub:athleteProfile:oneRepMaxVelocityOnboardingLearnMoreClick",
  /** Athlete profile - Latest load velocity profile onboarding tooltip shown */
  ATHLETE_PROFILE_PROFILING_VELOCITY_ONBOARDING_VIEW: "hub:athleteProfile:profilingVelocityOnboardingView",
  /** Athlete profile - Latest load velocity profile onboarding tooltip dismissed */
  ATHLETE_PROFILE_PROFILING_VELOCITY_ONBOARDING_DISMISS: "hub:athleteProfile:profilingVelocityOnboardingDismiss", // PROPERTIES: origin (closeButton, escapeKey)
  /** Athlete profile - Learn more link clicked in the latest load velocity profile onboarding tooltip */
  ATHLETE_PROFILE_PROFILING_VELOCITY_ONBOARDING_LEARN_MORE_CLICK:
    "hub:athleteProfile:profilingVelocityOnboardingLearnMoreClick",
};

const REPORT_EVENTS = {
  ATHLETE_REPORT_GENERATION: "hub:reports:athleteReportGenerate",
  GROUP_REPORT_GENERATION: "hub:reports:groupReportGenerate",
  REPORTS_VIEW_ALL: "hub:reports:viewAll",
};

const HUB_LIVE_EVENTS = {
  HUB_LIVE_VIEW: "hub:live:viewPage",
  HUB_LIVE_FILTER: "hub:live:filter", // PROPERTIES: filterGroupCount, filterAthleteCount
  HUB_LIVE_TOGGLE_PERIOD: "hub:live:togglePeriod", // PROPERTIES: period,
  HUB_LIVE_SET_LEADERBOARD_EXERCISE: "hub:live:setLeaderboardExercise", // PROPERTIES: exerciseId, eventClickLocation
  HUB_LIVE_ATHLETE_PROFILE_CHART_EXERCISE: "hub:live:athleteProfileChartExercise", // PROPERTIES: exerciseId, eventClickLocation
};

const GROUP_EVENTS = {
  GROUP_CREATE: "hub:group:create",
  GROUPS_VIEW_ALL: "hub:groups:viewAll",
  GROUPS_VIEW_ONE: "hub:groups:viewOne",
  GROUP_OVERVIEW_VIEW_DATA: "hub:groupOverview:viewChart",
};

const LEADERBOARD_EVENTS = {
  LEADERBOARD_CREATE: "hub:leaderboard:create",
  LEADERBOARDS_VIEW_ALL: "hub:leaderboards:viewAll",
};

const WORKOUT_EVENTS = {
  WORKOUT_CREATE: "hub:workout:create",
  WORKOUTS_VIEW_ALL: "hub:workouts:viewAll",
  WORKOUTS_LABEL_ASSIGN: "hub:workouts:labelAssign", // PROPERTIES: workoutsCount
  WORKOUTS_LABEL_UNASSIGN: "hub:workouts:labelUnassign", // PROPERTIES: workoutsCount
  WORKOUTS_LABEL_FILTER_CLOSE: "hub:workouts:LabelFilterClose", // PROPERTIES: workoutsLabelFilterCount
  WORKOUT_PREVIEW_ENTER_FULLSCREEN: "hub:workoutPreview:enterFullscreen",
  /** Workouts: Create workout from dialog */
  WORKOUT_CREATE_WORKOUT_FROM_DIALOG: "hub:workout:createFromDialog",
};

const WORKOUT_OVERVIEW_EVENTS = {
  /** Workout detail screen - Page view event for the workout review page. */
  WORKOUT_OVERVIEW_VIEW: "hub:workouts:viewWorkoutOverview",
  /** Workout detail screen - Event for when a user expands a measurement row in the workout review page. */
  WORKOUT_OVERVIEW_EXPAND_MEASUREMENT_ROW: "hub:workoutOverview:expandMeasurementRow",
};

const WORKOUT_REVIEW_EVENTS = {
  /** Workout review screen - Page view event. */
  WORKOUT_REVIEW_VIEW: "hub:workoutReview:viewPage",
};

const CUSTOM_EXERCISE_EVENTS = {
  CUSTOM_EXERCISE_CREATE: "hub:customExercises:create", // PROPERTIES: derivedFromExerciseId, isWeighted, isRepBased, isBilateral, location, isMigration
  CUSTOM_EXERCISE_V2_CREATE: "hub:customExercisesV2:create",
  CUSTOM_EXERCISE_V2_REVIEW: "hub:customExercisesV2:review",
  CUSTOM_EXERCISE_V2_UPDATE: "hub:customExercisesV2:update",
  CUSTOM_EXERCISE_V2_DELETE: "hub:customExercisesV2:delete",
};

const CIRCUIT_EVENTS = {
  CIRCUIT_CREATE: "hub:circuits:create",
  CIRCUIT_UPDATE: "hub:circuits:update",
  CIRCUIT_DELETE: "hub:circuits:delete",
};

const CHARTING_EVENTS = {
  CHARTING_V3_VIEW_PAGE: "hub:chartingV3:viewPage",
  CHARTING_V3_VIEW_DATA: "hub:chartingV3:viewData", // PROPERTIES": measureId, sourceCount, measureCount, period, measureType
  CHARTING_V3_VIEW_1RM_LINE: "hub:chartingV3:view1RMLine",
};

const OTHER_EVENTS = {
  INFO_CARD_VIEW: "hub:infoCard:view", // PROPERTIES: page
  HUB_GLOBAL_SEARCH: "hub:globalSearch:click", // PROPERTIES: section
  HUB_FAB_OPEN: "hub:fab:open",
  HUB_FAB_ACTION: "hub:fab:action", // PROPERTIES: actionName
  /** Drag and drop used in various places such as calendar, programming builder, workout edit, etc... */
  DRAG_DROP_START: "hub:dnd:dndStart", // PROPERTIES: Custom properties related to the drag item such as type (workout, exercise, session), taskType (exercise, note, video), origin (drawer, session), day, week
  /** Drag and drop target dropped on a valid target */
  DRAG_DROP_END_TARGET: "hub:dnd:dndEndTarget", // PROPERTIES: Custom properties related to the drag item such as target (session, day), day, week
  /** Drag and drop target unknown, meaning the item was not dropped on a valid target */
  DRAG_DROP_END_TARGET_UNKNOWN: "hub:dnd:dndEndTargetUnknown", // PROPERTIES: Custom related to the drag item
};

const DASHBOARD_EVENTS = {
  DASHBOARD_CREATE: "hub:dashboard:create", // Create new dashboard // Type of dashboard created // Group // Athlete
  DASHBOARD_NAME_UPDATE: "hub:dashboard:nameUpdate", // Edit dashboard name
  DASHBOARD_SELECTED_CHANGE: "hub:dashboard:selectedChange", // Change dashboard
  DASHBOARD_ATHLETE_UPDATE: "hub:dashboard:athleteFilterEdit", // Edit athlete
  DASHBOARD_ATHLETES_EXCLUDED: "hub:dashboard:athletesFilterExcluded", // Exclude athlete
  DASHBOARD_GROUP_UPDATE: "hub:dashboard:groupFilterEdit", // Edit group
  DASHBOARD_GROUP_RESET: "hub:dashboard:groupFilterReset", // Edit group
  DASHBOARD_TIME_FILTER_CHANGE: "hub:dashboard:timeFilterChange", // Time period filters
  DASHBOARD_TIME_FILTER_RESET: "hub:dashboard:timeFilterReset",
  DASHBOARD_ACCESS_UPDATED: "hub:dashboard:globalAccessUpdate", // Sharing
  DASHBOARD_DELETE: "hub:dashboard:delete",
  DASHBOARD_DUPLICATE: "hub:dashboard:duplicate",
  DASHBOARD_SET_DEFAULT: "hub:dashboard:setDefault",
  DASHBOARD_WIDGET_CREATE: "hub:dashboard:widgetCreate", // Type of widgets selected (when creating a dashboard) // Widgets created
  DASHBOARD_WIDGET_UPDATE: "hub:dashboard:widgetEdit", // Edit widget
  DASHBOARD_WIDGET_DELETE: "hub:dashboard:widgetDelete",
  DASHBOARD_WIDGET_DUPLICATE: "hub:dashboard:widgetDuplicate", // Duplicate widget
  DASHBOARD_PDF_REPORT_CREATED: "hub:dashboard:pdfReportCreated", // PDF report created
  /** Dashboards - Velocity-based 1RM announcement dialog shown */
  DASHBOARD_VELOCITY_ANNOUNCEMENT_VIEW: "hub:dashboard:velocityAnnouncementView",
  /** Dashboards - Velocity-based 1RM announcement dialog dismissed */
  DASHBOARD_VELOCITY_ANNOUNCEMENT_DISMISS: "hub:dashboard:velocityAnnouncementDismiss", // PROPERTIES: origin (closeButton, escapeKey, gotItButton)
  /** Dashboards - Knowledge base link clicked in the velocity-based 1RM announcement dialog */
  DASHBOARD_VELOCITY_ANNOUNCEMENT_LEARN_MORE_CLICK: "hub:dashboard:velocityAnnouncementLearnMoreClick",
};

const AI_SUMMARY_EVENTS = {
  AI_SUMMARY_GENERATE: "hub:dashboard:aiSummaryGenerate",
  AI_SUMMARY_REGENERATE: "hub:dashboard:aiSummaryRegenerate",
  /** Constant only — OS-13510 owns the edit UI and will dispatch this itself. */
  AI_SUMMARY_EDIT: "hub:dashboard:aiSummaryEdit",
  /** Constant only — OS-13510 owns the export UI and will dispatch this itself. */
  AI_SUMMARY_EXPORT_WITH_SUMMARY: "hub:dashboard:aiSummaryExportWithSummary",
  AI_SUMMARY_FEEDBACK: "hub:dashboard:aiSummaryFeedback", // PROPERTIES: traceId, feedbackValue
};

const PROGRAMMING_EVENTS = {
  /** Programming - Create program */
  PROGRAMMING_LIST_CREATE: "hub:programming:programCreate",
  /** Programming - Edit from programs list */
  PROGRAMMING_LIST_EDIT: "hub:programming:programEdit",
  /** Programming -  Delete from programs list */
  PROGRAMMING_LIST_DELETE: "hub:programming:programDelete",
  /** Programming -  Duplicate from programs list */
  PROGRAMMING_LIST_DUPLICATE: "hub:programming:programDuplicate",
  /** Programming - Click program row from programs list */
  PROGRAMMING_LIST_ROW_CLICK: "hub:programming:programRowClick",
  /** Programming - Change filter from programs list */
  PROGRAMMING_LIST_FILTER: "hub:programming:programFilter", // PROPERTIES: searchText, createdBy, scheduleFor

  // Programming - Program Overview
  /** Programming - View program overview */
  PROGRAMMING_OVERVIEW_VIEW_OVERVIEW: "hub:programming:programViewOverview",
  /** Programming - Navigate to calendar from program overview */
  PROGRAMMING_OVERVIEW_OPEN_CALENDAR: "hub:programming:programOpenCalendar",
  /** Programming - Schedule program from program overview */
  PROGRAMMING_OVERVIEW_SCHEDULE: "hub:programming:programSchedule", // PROPERTIES: type (athlete,group)
  /** Programming - Clicked edit program from calendar from program overview */
  PROGRAMMING_OVERVIEW_EDIT_FROM_OVERVIEW: "hub:programming:programEditFromOverview",
  /** Programming - Delete program from program overview */
  PROGRAMMING_OVERVIEW_DELETE_FROM_OVERVIEW: "hub:programming:programDeleteFromOverview",
  /** Programming - Delete program from program overview start flow */
  PROGRAMMING_OVERVIEW_DELETE_FROM_OVERVIEW_START: "hub:programming:programDeleteFromOverviewStart",
  /** Programming - Delete program from program overview with active programs in progress */
  PROGRAMMING_OVERVIEW_DELETE_FROM_OVERVIEW_PROGRAM_IN_PROGRESS:
    "hub:programming:programDeleteFromOverviewProgramInProgress",
  /** Programming - Change filter from program overview */
  PROGRAMMING_OVERVIEW_FILTER: "hub:programming:programOverviewFilter", // PROPERTIES: status, searchText
  /** Programming - Exclude program from calendar from program overview */
  PROGRAMMING_OVERVIEW_SCHEDULE_EXCLUDE: "hub:programming:programScheduleExclude",
  /** Programming - Edit program attendees from program overview */
  PROGRAMMING_OVERVIEW_EDIT_ATTENDEES: "hub:programming:programEditAttendees",
  /** Programming - Calendar event status click from program overview */
  PROGRAMMING_OVERVIEW_EVENT_STATUS_CLICK: "hub:programming:programOverviewEventStatusClick", // PROPERTIES: status (completed, missed...)

  // Programming - Builder
  /** Programming - Collapse all action clicked */
  PROGRAMMING_BUILDER_COLLAPSE_ALL: "hub:programming:collapseAll",
  /** Programming - Program name update start from builder */
  PROGRAMMING_BUILDER_PROGRAM_NAME_UPDATE_START: "hub:programming:builderProgramNameUpdateStart", // PROPERTIES: origin (inlineEdit, editIcon)
  /** Programming - Program name update from builder */
  PROGRAMMING_BUILDER_PROGRAM_NAME_UPDATE: "hub:programming:builderProgramNameUpdate",
  /** Programming - Program description update from builder */
  PROGRAMMING_BUILDER_PROGRAM_DESCRIPTION_UPDATE_START: "hub:programming:builderProgramDescriptionUpdateStart", // PROPERTIES: origin (inlineEdit, editIcon)
  /** Programming - Program description update from builder */
  PROGRAMMING_BUILDER_PROGRAM_DESCRIPTION_UPDATE: "hub:programming:builderProgramDescriptionUpdate",
  /** Programming - Delete exercise from builder menu item clicked */
  PROGRAMMING_BUILDER_PROGRAM_DELETE_START: "hub:programming:builderExerciseDeleteStart",
  /** Programming - Delete exercise from builder */
  PROGRAMMING_BUILDER_PROGRAM_DELETE: "hub:programming:builderExerciseDelete",
  //
  /** Programming - Week name update from builder */
  PROGRAMMING_BUILDER_WEEK_DESCRIPTION_UPDATE_START: "hub:programming:builderWeekDescriptionUpdateStart", // PROPERTIES: origin (inlineEdit, editIcon)
  /** Programming - Week name update from builder */
  PROGRAMMING_BUILDER_WEEK_DESCRIPTION_UPDATE: "hub:programming:builderWeekDescriptionUpdate",
  /** Programming - Program add week action clicked */
  PROGRAMMING_BUILDER_WEEK_ADD: "hub:programming:builderWeekAdd",
  /** Programming - Program duplicate week actions clicked */
  PROGRAMMING_BUILDER_WEEK_COPY: "hub:programming:builderWeekCopy",
  /** Programming - Delete session flow started */
  PROGRAMMING_BUILDER_WEEK_DELETE_START: "hub:programming:builderWeekDeleteStart",
  /** Programming - Delete session from builder */
  PROGRAMMING_BUILDER_WEEK_DELETE: "hub:programming:builderWeekDelete",
  //
  /** Programming - Session add step action clicked */
  PROGRAMMING_BUILDER_SESSION_ADD_STEP_START: "hub:programming:builderSessionAddStepStart",
  /** Programming - Filter sessions in add step drawer from builder */
  PROGRAMMING_BUILDER_ADD_STEPS_DRAWER_FILTER: "hub:programming:builderAddStepsDrawerFilter", // PROPERTIES: searchText, category
  /** Programming - Build new session from builder */
  PROGRAMMING_BUILDER_SESSION_CREATE: "hub:programming:builderSessionCreate",
  /** Programming - Filter library sessions from builder */
  PROGRAMMING_BUILDER_IMPORT_LIBRARY_DRAWER_FILTER: "hub:programming:builderImportLibraryDrawerFilter", // PROPERTIES: searchText, sortBy, createdBy
  /** Programming - Import library session from builder */
  PROGRAMMING_BUILDER_SESSION_IMPORT_LIBRARY_START: "hub:programming:builderSessionImportLibraryStart",
  /** Programming - Add rest day session from builder */
  PROGRAMMING_BUILDER_SESSION_ADD_REST_DAY: "hub:programming:builderSessionAddRestDay",
  //
  /** Programming - Save as new workout template flow started */
  PROGRAMMING_BUILDER_SESSION_SAVE_TEMPLATE_START: "hub:programming:builderSessionSaveTemplateStart",
  /** Programming - Save as new workout template from builder */
  PROGRAMMING_BUILDER_SESSION_SAVE_TEMPLATE: "hub:programming:builderSessionSaveTemplate",
  /** Programming - Session added from drawer from builder */
  PROGRAMMING_BUILDER_ADD_FROM_DRAWER: "hub:programming:builderAddFromDrawer", // PROPERTIES: type (workout, exercise), taskType (exercise, note, video), origin (libraryDrawer, workoutDrawer)
  /** Programming - Session viewed from drawer from builder */
  PROGRAMMING_BUILDER_LIBRARY_DRAWER_CHANGE_VIEW: "hub:programming:builderLibraryDrawerChangeView",
  /** Programming - Create a new exercise from drawer from builder */
  PROGRAMMING_BUILDER_EXERCISE_CREATE_FROM_DRAWER: "hub:programming:builderExerciseCreateFromDrawer",
  //
  /** Programming - Duplicate session from builder */
  PROGRAMMING_BUILDER_SESSION_DUPLICATE: "hub:programming:builderSessionDuplicate",
  /** Programming - Rename workout flow started */
  PROGRAMMING_BUILDER_SESSION_RENAME_START: "hub:programming:builderWorkoutRenameStart",
  /** Programming - Rename workout from builder */
  PROGRAMMING_BUILDER_SESSION_RENAME: "hub:programming:builderWorkoutRename",
  /** Programming - Delete session from builder */
  PROGRAMMING_BUILDER_SESSION_DELETE_START: "hub:programming:builderSessionDeleteStart",
  /** Programming - Delete session from builder */
  PROGRAMMING_BUILDER_SESSION_DELETE: "hub:programming:builderSessionDelete",
  //
  /** Programming - Duplicate session from builder */
  PROGRAMMING_BUILDER_WORKOUT_STEP_DUPLICATE: "hub:programming:builderWorkoutStepDuplicate",
  /** Programming - Delete session from builder */
  PROGRAMMING_BUILDER_WORKOUT_STEP_DELETE_START: "hub:programming:builderWorkoutStepDeleteStart",
  /** Programming - Delete session from builder */
  PROGRAMMING_BUILDER_WORKOUT_STEP_DELETE: "hub:programming:builderWorkoutStepDelete",
  /** Programming - Swap exercise flow started */
  PROGRAMMING_BUILDER_WORKOUT_STEP_SWAP_EXERCISE_START: "hub:programming:builderWorkoutStepSwapExerciseStart", // PROPERTIES: origin (swapIconButton, exerciseTaskMenu)
  /** Programming - Swap exercise from builder */
  PROGRAMMING_BUILDER_WORKOUT_STEP_SWAP_EXERCISE: "hub:programming:builderWorkoutStepSwapExercise",

  // Programming - Program Import
  /** Programming - Program import: opened import modal (+ on Day -> Import Program) */
  PROGRAMMING_IMPORT_OPEN: "hub:programming:importOpen", // PROPERTIES: origin
  /** Programming - Program import: file added (picker or drag-drop) */
  PROGRAMMING_IMPORT_ADD_FILE: "hub:programming:importAddFile", // PROPERTIES: fileType
  /** Programming - Program import: program generated from parsed file */
  PROGRAMMING_IMPORT_GENERATE_PROGRAM: "hub:programming:importGenerateProgram", // PROPERTIES: fileType, matchedExerciseCount, unmatchedExerciseCount, matchedCircuitCount, unmatchedCircuitCount, workoutsCount
  /** Programming - Program import: import/create program button clicked */
  PROGRAMMING_IMPORT_PROGRAM: "hub:programming:importProgram", // PROPERTIES: importTarget, workoutsCount, circuitStepsCount
};

const CALENDAR_EVENTS = {
  /** Calendar - Change entity (athlete or group) from calendar */
  CALENDAR_ENTITY_CHANGE: "hub:calendar:entityChange", // PROPERTIES: searchText (boolean indicating whether there is text in the search input)
  /** Calendar - Change search tab from calendar */
  CALENDAR_SEARCH_TAB_CHANGE: "hub:calendar:searchTabChange",
  /** Calendar - Go to today button clicked from calendar */
  CALENDAR_GO_TO_TODAY: "hub:calendar:goToToday",
  /** Calendar - Month switch from calendar */
  CALENDAR_MONTH_SWITCH: "hub:calendar:monthSwitch",
  /** Calendar - View event from calendar */
  CALENDAR_EVENT_VIEW: "hub:calendar:eventView", // PROPERTIES: eventType
  /** Calendar - View completed event from calendar */
  CALENDAR_EVENT_COMPLETED_VIEW: "hub:calendar:completedEventView",
  /** Calendar - Edit event name flow start from calendar */
  CALENDAR_EVENT_RENAME_EDIT_START: "hub:calendar:eventRenameEdit",
  /** Calendar - Edit event name from calendar */
  CALENDAR_EVENT_RENAME_EDIT: "hub:calendar:eventRenameEdit",
  /** Calendar - Edit event name flow start from workout edit event */
  CALENDAR_EVENT_EDIT_SESSION_RENAME_EDIT_START: "hub:calendar:eventEditSessionRenameEditStart",
  /** Calendar - Edit event name from workout edit event */
  CALENDAR_EVENT_EDIT_SESSION_RENAME_EDIT: "hub:calendar:eventEditSessionRenameEdit",
  /** Calendar - Delete event from calendar start */
  CALENDAR_EVENT_DELETE_START: "hub:calendar:eventDeleteStart",
  /** Calendar - Delete event from calendar */
  CALENDAR_EVENT_DELETE: "hub:calendar:eventDelete", // PROPERTIES: eventType
  /** Calendar - Duplicate event start from calendar */
  CALENDAR_EVENT_DUPLICATE_START: "hub:calendar:eventDuplicateStart",
  /** Calendar - Duplicate event from calendar */
  CALENDAR_EVENT_DUPLICATE: "hub:calendar:eventDuplicate",
  /** Calendar - Revert event to original flow start from calendar */
  CALENDAR_EVENT_REVERT_START: "hub:calendar:eventRevertStart",
  /** Calendar - Revert event to original */
  CALENDAR_EVENT_REVERT: "hub:calendar:eventRevert", // PROPERTIES: eventType
  /** Calendar - Save as template start flow from calendar */
  CALENDAR_EVENT_SAVE_TEMPLATE_START: "hub:calendar:eventSaveTemplateStart",
  /** Calendar - Save as template from calendar */
  CALENDAR_EVENT_SAVE_TEMPLATE: "hub:calendar:eventSaveTemplate",
  /** Calendar - Save as template start flow from workout edit event */
  CALENDAR_EVENT_EDIT_SESSION_SAVE_TEMPLATE_START: "hub:calendar:eventEditSessionSaveTemplateStart",
  /** Calendar - Save as template from workout edit event */
  CALENDAR_EVENT_EDIT_SESSION_SAVE_TEMPLATE: "hub:calendar:eventEditSessionSaveTemplate",
  /** Calendar - Navigate to program overview from calendar event */
  CALENDAR_EVENT_VIEW_PROGRAM: "hub:calendar:eventViewProgram",
  /** Calendar - View attendees list from calendar event */
  CALENDAR_EVENT_VIEW_ATTENDEES: "hub:calendar:eventViewAttendees",
  /** Calendar - Include all attendees from calendar event */
  CALENDAR_EVENT_ATTENDEE_INCLUDE_ALL: "hub:calendar:eventAttendeeIncludeAll",
  /** Calendar - Add attendees from calendar event */
  CALENDAR_EVENT_ATTENDEE_INCLUDE: "hub:calendar:eventAttendeeInclude",
  /** Calendar - Remove attendees from calendar event */
  CALENDAR_EVENT_ATTENDEE_EXCLUDE: "hub:calendar:eventAttendeeExclude",
  /** Calendar - Change event date from calendar */
  CALENDAR_EVENT_CHANGE_DATE: "hub:calendar:eventChangeDate",
  /** Calendar - Add button clicked from calendar event drawer */
  CALENDAR_EVENT_ADD_FROM_DRAWER: "hub:calendar:eventAddFromDrawer", // PROPERTIES: eventSourceType, origin
  /** Calendar - Edit workout from calendar event */
  CALENDAR_EVENT_EDIT_WORKOUT: "hub:calendar:eventEditWorkout",
  /** Calendar - Edit workout add action from calendar event */
  CALENDAR_EVENT_EDIT_WORKOUT_ADD: "hub:calendar:eventEditWorkoutAdd", // PROPERTIES: taskType
  /** Calendar - View full details link from the completed drawer */
  CALENDAR_EVENT_VIEW_FULL_DETAILS_CLICK: "hub:calendar:eventViewFullDetailsClick",
  /** Calendar - View workout link from the completed drawer */
  CALENDAR_EVENT_VIEW_WORKOUT_CLICK: "hub:calendar:eventViewWorkoutClick",
  /** Calendar - Create exercise from workout edit calendar event */
  CALENDAR_EXERCISE_CREATE_FROM_DRAWER: "hub:calendar:exerciseCreateFromDrawer",
  /** Calendar - View compliance event from calendar. */
  CALENDAR_COMPLIANCE_EVENT_VIEW: "hub:calendar:complianceEventView", // PROPERTIES: eventKind
};

/**
 * Every upsell event carries UPSELL_FEATURE and UPSELL_PLAN_TYPE so conversion
 * can be analysed per tier and per locked feature.
 */
const UPSELL_EVENTS = {
  UPSELL_LOCKED_FEATURE_CLICKED: "hub:upsell:lockedFeatureClicked", // PROPERTIES: upsellFeature, upsellPlanType
  UPSELL_PAGE_TIME_SPENT: "hub:upsell:pageTimeSpent", // PROPERTIES: upsellFeature, upsellPlanType, timeSpentSeconds
  UPSELL_REQUEST_ACCESS_CLICKED: "hub:upsell:requestAccessClicked", // PROPERTIES: upsellFeature, upsellPlanType, origin
};

const SUBSCRIPTION_EVENTS = {
  /** Subscription - Buy more sensors user action clicked */
  SUBSCRIPTION_BUY_MORE_SENSORS: "hub:subscription:buyMoreSensors", // PROPERTIES: origin (settings, menu)
  /** Subscription - Sensor upsell banner dismissed */
  SUBSCRIPTION_SENSOR_UPSELL_BANNER_DISMISS: "hub:subscription:sensorUpsellBannerDismiss",
  /** Subscription - Sensor upsell banner clicked */
  SUBSCRIPTION_SENSOR_UPSELL_BANNER_CLICK: "hub:subscription:sensorUpsellBannerClick",
};

const CONNECT_EVENTS = {
  /** User completed the /connect handshake, linking their Hub account to an MCP client */
  MCP_CLIENT_CONNECTED: "hub:connect:mcpClientConnected",
};

export const EVENTS = {
  ...ATHLETE_EVENTS,
  ...REPORT_EVENTS,
  ...HUB_LIVE_EVENTS,
  ...GROUP_EVENTS,
  ...LEADERBOARD_EVENTS,
  ...WORKOUT_OVERVIEW_EVENTS,
  ...WORKOUT_EVENTS,
  ...WORKOUT_REVIEW_EVENTS,
  ...CUSTOM_EXERCISE_EVENTS,
  ...CIRCUIT_EVENTS,
  ...CHARTING_EVENTS,
  ...OTHER_EVENTS,
  ...DASHBOARD_EVENTS,
  ...AI_SUMMARY_EVENTS,
  ...PROGRAMMING_EVENTS,
  ...CALENDAR_EVENTS,
  ...UPSELL_EVENTS,
  ...SUBSCRIPTION_EVENTS,
  ...CONNECT_EVENTS,
} as const;

export type EventNames = (typeof EVENTS)[keyof typeof EVENTS];
